import { useEffect, useMemo, useState } from "react";
import { Link, To, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Step,
  StepIcon,
  StepLabel,
  Stepper,
  Theme,
  makeStyles,
} from "@material-ui/core";
import {
  HomeOutlined,
  InfoOutlined,
  SummarizeOutlined,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import {
  Button,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import React from "react";
import useQueryState from "../../hooks/useQueryState";
import Login from "./partials/Login/Auth";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuthContext } from "../../hooks/useAuthContext";
import useAuth from "../../hooks/useAuth";

const Sidebar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const {
    queryState: { ui },
    queryState,
    setQueryState,
  } = useQueryState();
  const { mainSidebar, mapSidebar } = ui || {};

  const { postLogout } = useAuth();
  const { parsedToken } = useAuthContext();
  const { email: userEmail } = parsedToken || {};

  const useStyles = makeStyles((theme: Theme) => ({
    link: {
      textDecoration: "none",
      color: "#2D3648",
      fontSize: "14px",
      padding: "12px 0",
      width: "100%",
    },
    activeLink: {
      backgroundColor: "#e5ebf2",
    },
    hoveredLink: {
      "&:hover": {
        background: "#E5E5E5",
      },
    },
  }));

  const classes = useStyles();

  const isDashboard = location.pathname.startsWith("/dashboard");
  const isActiveLink = (pathname: string) => {
    return location.pathname === pathname;
  };

  const dashboardSteps = useMemo(
    () => [
      {
        label: "Home",
        link: "/home",
        icon: <HomeOutlined />,
      },
      {
        label: "User Guide",
        link: "/disclaimer",
        icon: <SummarizeOutlined />,
      },
      {
        label: "NGDI Dashboard",
        link: "/dashboard/get-started",
        icon: <SpaceDashboardOutlined />,
        child: <NGDIDashboardStep isExpanded={mainSidebar} />,
      },
      {
        label: "About",
        link: "/about",
        icon: <InfoOutlined />,
      },
    ],
    [mainSidebar],
  );

  const handleSidebar = () =>
    setQueryState({
      ui: {
        ...queryState.ui,
        mainSidebar: !mainSidebar,
      },
    });

  const handleLogin = () => {
    if (userEmail) return;
    setIsOpen(true);
  };

  const handleLogout = async () => {
    try {
      const res = await postLogout.mutateAsync();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Stack
      sx={{
        width: mainSidebar ? 256 : 68,

        flexShrink: 0,
        borderRight: "1px solid #CFDBE2",

        display: "flex",
        flexDirection: "row",
        bgcolor: "#F5F8F9",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        // position: "relative",
      }}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        height="100%"
        sx={{
          bgcolor: "#F5F8F9",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: mainSidebar ? "256px" : "68px",
            flex: 1,
            bgcolor: "#F5F8F9",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={mainSidebar ? "flex-start" : "center"}
            height="80px"
            paddingLeft={mainSidebar ? 2 : 0}
          >
            {mainSidebar ? (
              <Typography variant="h3">NGDI Dashboard</Typography>
            ) : (
              <MoreHorizIcon />
            )}
          </Stack>
          <Stack
            justifyContent="center"
            alignItems="center"
            height="44px"
            direction="row"
            gap={1}
            px={2}
          >
            <Button
              variant={userEmail ? "text" : "outlined"}
              size="small"
              sx={{
                width: mainSidebar ? "224px" : "48px",
                minWidth: mainSidebar ? undefined : "48px",
                padding: userEmail ? 0 : undefined,
              }}
              onClick={handleLogin}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <AccountCircleIcon fontSize="small" />
                {mainSidebar && (
                  <Typography
                    sx={{
                      maxWidth: 140,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {userEmail || "Login"}
                  </Typography>
                )}
              </Stack>
            </Button>
            {userEmail && mainSidebar && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  flex: 1,
                  minWidth: postLogout.isLoading ? "36px" : "20px",
                  color: "#E53935",
                  borderColor: "#E53935",
                  p: postLogout.isLoading ? 0 : undefined,
                  px: postLogout.isLoading ? undefined : mainSidebar ? 2 : 0,
                  "&:hover": {
                    backgroundColor: postLogout.isLoading ? "white" : "#E53935",
                    color: "#fff",
                    borderColor: "#E53935",
                  },
                }}
                onClick={handleLogout}
                disabled={postLogout.isLoading}
              >
                {postLogout.isLoading ? (
                  <CircularProgress size={15} sx={{ color: "red" }} />
                ) : (
                  <LogoutIcon fontSize="small" />
                )}
              </Button>
            )}
          </Stack>
          <Box>
            {dashboardSteps.map((step, index) => {
              return (
                <Stack
                  direction="column"
                  justifyContent="center"
                  key={`sidebar-step-${index}`}
                >
                  {/* <Box flex={1}> */}
                  <Link
                    to={{ pathname: step.link, search: location.search }}
                    className={`${classes.link} ${
                      isActiveLink(step.link) && classes.activeLink
                    } ${classes.hoveredLink}`}
                    key={`sidebar-link-${index}`}
                  >
                    <Tooltip
                      title={mainSidebar ? null : step.label}
                      placement="right"
                    >
                      <Stack
                        direction="row"
                        paddingLeft="20px"
                        gap={2}
                        alignItems="center"
                      >
                        <Box>{step.icon}</Box>
                        <Typography variant="h6">
                          {mainSidebar ? step.label : ""}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </Link>
                  {/* </Box> */}
                  <Box
                    flex={1}
                    // sx={{ mb: isDashboard && step.child ? 1 : undefined }}
                  >
                    {isDashboard && step.child}
                  </Box>
                </Stack>
              );
            })}
          </Box>
        </Box>
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        onClick={handleSidebar}
        sx={{
          cursor: "pointer",
          position: "absolute",
          zIndex: 1,
          bgcolor: "#F5F8F9",
          transform: `translate(${mainSidebar ? "252px" : "65px"}, 39vh)`,
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            zIndex: 1,
            backgroundColor: "#F8F8F8",
            borderRadius: "0 6px 6px 0",
            padding: "5px",
            width: "25px",
            height: "50px",
          }}
          onClick={handleSidebar}
        >
          {mainSidebar ? (
            <ChevronLeft sx={{ color: "#3f51b5" }} />
          ) : (
            <ChevronRight sx={{ color: "#3f51b5" }} />
          )}
        </IconButton>
      </Stack>
      <Login isOpen={isOpen} setIsOpen={setIsOpen} />
    </Stack>
  );
};

const NGDIDashboardStep = ({ isExpanded }: { isExpanded: boolean }) => {
  const [activeStep, setActiveStep] = React.useState<number>(3);
  const location = useLocation();
  const useStyles = makeStyles((theme: Theme) => ({
    stepIconContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    stepper: {
      "& .MuiStepConnector-vertical": {
        marginLeft: 0,
      },
      "& .MuiStepConnector-line": {
        marginLeft: "calc(50% - 4px)",
        borderColor: "#81819C",
        width: "1px",
      },
    },
    step: {
      "& .MuiStepLabel-vertical": {
        display: "flex",
        flexDirection: "column",
        gap: "3px",
      },
    },
    activeStep: {
      "& .MuiStepLabel-vertical": {
        display: "flex",
        flexDirection: "column",
        gap: "3px",
      },
      "& .MuiStepLabel-label .MuiTypography-body1": {
        color: "#000",
        fontWeight: "600",
      },
    },
    activeStepLabel: {
      fontWeight: "bold",
      color: "#000",
      textAlign: "center",
    },
    stepLabel: {
      marginTop: theme.spacing(1),
      textAlign: "center",
    },
    stepContent: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      textAlign: "center",
    },
  }));

  const classes = useStyles();

  const steps = [
    {
      label: "Get Started",
      description: "Define what data you are especially interested in.",
      link: "/dashboard/get-started",
    },
    {
      label: "Identify Drought Risk Areas",
      description:
        "Explore the drought indices for selected areas and time periods.",
      link: "/dashboard/drought-risk",
    },
    {
      label: "Understand Drought Indices",
      description:
        "Learn about various drought indices and their implications.",
      link: "/dashboard/drought-indices",
    },
    {
      label: "Analyse Financial Indices",
      description:
        "Examine financial indices related to drought impacts and management.",
      link: "/dashboard/financial-indices",
    },
  ];

  const navigate = useNavigate();

  useEffect(() => {
    const currentStepIndex = steps.findIndex((step) =>
      location.pathname.includes(step.link),
    );
    if (currentStepIndex !== -1) {
      setActiveStep(currentStepIndex);
    }
  }, [location]);

  const handleStepClick = (index: React.SetStateAction<number>, path: To) => {
    setActiveStep(index);

    navigate({
      pathname: `${path}`,
      search: location.search,
    });
  };

  return (
    <Box>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        className={classes.stepper}
        style={{ background: "#F5F8F9", zIndex: 2 }}
      >
        {steps.map((step, index) => (
          <Step
            key={step.label}
            onClick={() => handleStepClick(index, step.link)}
            className={activeStep === index ? classes.activeStep : classes.step}
          >
            <Tooltip title={isExpanded ? null : step.label} placement="right">
              <StepLabel
                StepIconComponent={(props) => (
                  <Box className={classes.stepIconContainer}>
                    <StepIcon {...props}>
                      <Typography variant="body1" style={{ fontSize: "12px" }}>
                        {index + 1}
                      </Typography>
                    </StepIcon>
                  </Box>
                )}
                classes={{
                  label: `${classes.stepLabel} ${
                    activeStep === index ? classes.activeStepLabel : ""
                  }`,
                }}
                style={{
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                {isExpanded && (
                  <>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {step.label}
                    </Typography>
                    <Box
                      className={classes.stepContent}
                      style={{ cursor: "pointer" }}
                    >
                      <Typography variant="body3" color="#717D96">
                        {step.description}
                      </Typography>
                    </Box>
                  </>
                )}
              </StepLabel>
            </Tooltip>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default Sidebar;
